(ns app.front.view.group.admin.inspections.core
  (:require [app.domain.group.model :as group-model]
            [app.domain.group.permit-type-config.model :as ptc-model]
            [app.domain.inspection.model :as inspection-model]
            [app.domain.label.model :as label-model]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.label-group-state :as label-group-state]
            [app.front.ui.section :as section]
            [app.front.ui.tabs :as ui-tabs]
            [app.front.util :as futil]
            [app.front.view.group.admin.common :as acommon]
            [app.front.view.group.admin.config.counter-config-editor :as counter-config-editor]
            [app.front.view.group.admin.feature-toggle :as feature-toggle]
            [app.front.view.group.admin.inspections.checklists.checklists :as inspection-checklists]
            [app.front.view.group.admin.inspections.checklists.checklists-v2 :as inspection-checklists-v2]
            [app.front.view.group.admin.inspections.checklists.subs :as checklist-subs]
            [app.front.view.group.admin.inspections.freeze-time-editor :as inspection-freeze-time-editor]
            [app.front.view.group.admin.inspections.inspection-labels :as inspection-labels]
            [app.front.view.group.admin.inspections.inspection-phases :as inspection-phases]
            [app.front.view.group.admin.inspections.instructions :as inspection-instructions-editor]
            [app.front.view.group.admin.inspections.qualification :as qualification]
            [app.front.view.group.admin.inspections.quick-pick-deficiencies :as quick-pick-deficiencies]
            [app.front.view.group.admin.inspections.subs :as ias]
            [app.front.view.group.admin.inspections.template-editor :as template-editor]
            [app.front.view.group.admin.inspections.time-slots-editor :as inspection-time-slots-editor]
            [app.front.view.group.admin.inspections.visit-result :as visit-result]
            [app.front.view.group.admin.inspections.visit-result-detail :as visit-result-detail]
            [app.front.view.group.admin.inspections.visit-type :as visit-type]
            [app.front.view.group.admin.inspections.working-areas :as working-areas]
            [app.front.view.group.admin.phrases :as phrases]
            [app.front.view.group.admin.subs :as gas]
            [app.front.view.inspections.events :as inspection-events]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]
            [schema.core :as s]))

;;; Events

(rf/reg-event-fx ::fetch-inspections
  (fn [_ [_ group-id]]
    {:comm/command {:command      :inspection/get-inspections
                    :method       :get
                    :data         {:group-id group-id}
                    :on-success   [::inspection-events/inspections-loaded]
                    ;; This gets called by the route controller for templates even when the group does not have
                    ;; inspections, so we just ignore the possible error
                    :on-not-found [:comm/noop]}}))

(rf/reg-event-fx ::fetch-inspector-users
  (fn [_ [_ group-id]]
    {:comm/command {:command    :group/inspection-groups-and-users
                    :method     :get
                    :data       {:authority-group-id group-id}
                    :on-success [:app.front.entities/merge-entity-db]}}))

(rf/reg-event-fx ::fetch-counter-configs
  (rf/path ::state)
  (fn [_ [_ group-id]]
    {:comm/command {:command    :inspection/get-counter-configs
                    :method     :get
                    :data       {:authority-group-id group-id}
                    :on-success [:app.front.entities/merge-entity-db]}}))

(rf/reg-event-fx ::init
  (fn [_ [_ group-id]]
    {:dispatch-n [[::fetch-inspections group-id]
                  [::working-areas/fetch-working-areas group-id]
                  [::fetch-inspector-users group-id]
                  [::fetch-counter-configs group-id]]}))

(rf/reg-event-fx ::set-comply-by-date-email-notification-days
  (fn [_ [_ group-id value]]
    (let [value (cond
                  (nil? value) nil
                  (and (string? value) (str/blank? value)) 0
                  (number? value) (max 0 value)
                  :else 0)]
      {:comm/command {:command    :group/set-comply-by-date-email-notification-days
                      :data       (util/mmap group-id value)
                      :on-success [::entities/apply-changes]}})))

(rf/reg-sub ::can-hide-inspections-from-applicant?
  :<- [::gas/group-permit-types]
  (fn [permit-types _]
    (contains? permit-types :permit-type/L)))

(rf/reg-sub ::inspection-checklist-visibility
  :<- [::gas/group]
  :-> :group/inspection-checklist-visibility)

(rf/reg-event-fx ::set-inspection-checklist-visibility
  (fn [_ [_ group-id value]]
    {:comm/command {:command    :group/set-inspection-checklist-visibility
                    :data       (util/mmap group-id value)
                    :on-success [::entities/apply-changes]}}))

(rf/reg-sub ::selected-tab
  :-> ::selected-tab)

(rf/reg-event-fx ::select-tab
  (fn [{:keys [db]} [_ tab]]
    (let [group-id (::gas/group-id db)
          group    (entities/find-entity (entities/db->context db) group-id)
          labels?  (and group (label-model/labels-enabled-for-group? group))]
      (cond-> {:db (assoc db ::selected-tab tab)}
        (and (= tab :inspection/label-configuration) labels?)
        (assoc :dispatch [::label-group-state/fetch-label-groups group-id])))))

(rf/reg-sub ::inspection-restrictions-available?
  :<- [::ff/enabled? :feature-flag.id/inspection-restriction]
  :<- [::ff/enabled? :feature-flag.id/inspection-restriction-mobile]
  :<- [::gas/has-feature? :group.feature/mobile-app]
  group-model/has-mobile-feature?)

(rf/reg-sub ::comply-by-date-email-notification-days
  :<- [::gas/group]
  :-> :group/comply-by-date-email-notification-days)

;;; Components

(defn inspection-checklist-visibility [{:keys [tr authority-group-id]}]
  [:div.mb-4
   (into
     [ui/select-pills {:label-text (tr [:inspection-checklist-visibility/config-label])
                       :on-change  #(>evt [::set-inspection-checklist-visibility authority-group-id %])
                       :value      (or (<sub [::inspection-checklist-visibility])
                                       :inspection-checklist-visibility/hide)}]
     (for [value inspection-model/checklist-visibility-values]
       {:value value
        :text  (tr [value])}))])

(defn numbering-configuration [tr ctx group-feature-toggle]
  (let [numbering-enabled?            (<sub [::ff/enabled? :feature-flag.id/deficiency-photo-numbering])
        deficiency-numbering-enabled? (<sub [::gas/has-feature? :group.feature/deficiency-numbering])
        photo-numbering-enabled?      (<sub [::gas/has-feature? :group.feature/photo-numbering])
        permit-types                  (<sub [::gas/group-permit-types])]
    (when numbering-enabled?
      [:<>
       (when (empty? (set/intersection inspection-model/permit-types-without-deficiencies permit-types))
         [:<>
          (group-feature-toggle :group.feature/deficiency-numbering)
          (when deficiency-numbering-enabled?
            [:<>
             [:div.mb-2.text-sm (tr [:deficiencies/deficiency-numbering-instruction])]
             [counter-config-editor/counter-config-editor
              {:tr                  tr
               :group-id            (:authority-group-id ctx)
               :counter-config-type :counter-config.type/deficiency
               :custom-data-tr-key  :workspace/municipal-case-id}]])])
       (group-feature-toggle :group.feature/photo-numbering)
       (when photo-numbering-enabled?
         [:<>
          [:div.mb-2.text-sm (tr [:inspection/photo-numbering-instruction])]
          [counter-config-editor/counter-config-editor
           {:tr                  tr
            :group-id            (:authority-group-id ctx)
            :counter-config-type :counter-config.type/inspection-photo
            :custom-data-tr-key  :workspace/municipal-case-id}]])])))

(defn comply-by-date-email-notification [{:keys [tr authority-group-id]}]
  (let [days     (<sub [::comply-by-date-email-notification-days])
        enabled? (some? days)]
    [:div.mb-4
     [:h3.w-full.mb-2 (tr [:group.feature.title/comply-by-date-email-notification-days])]
     [:div.mb-2.text-xs (tr [:group.feature/comply-by-date-email-notification-days])]
     [:div.flex.items-center.w-full
      [:div.flex-none.w-24
       [cc/toggle {:id         "toggle-comply-by-emails"
                   :disabled   false
                   :toggled    enabled?
                   :aria-label (tr [:group.feature.title/comply-by-date-email-notification-days])
                   :class-name "unlabeled-toggle"
                   :on-toggle  (fn [checked]
                                 (>evt [::set-comply-by-date-email-notification-days
                                        authority-group-id
                                        (when checked
                                          (or days inspection-model/comply-by-email-default-days))]))}]]
      [:div.w-24.mb-2
       [ui/number-input
        {:value       (or days "")
         :min         0
         :disabled    (not enabled?)
         :placeholder inspection-model/comply-by-email-default-days
         :on-change   #(let [v (futil/tval %)]
                         (>evt [::set-comply-by-date-email-notification-days
                                authority-group-id
                                (if (str/blank? v)
                                  0
                                  (futil/parse-int v))]))}]]]]))

(defn inspection-settings
  "General inspection settings that don't need a separate config page of their own"
  [ctx]
  (let [tr                                    (<sub [::gas/group-tr])
        can-hide-inspections?                 (<sub [::can-hide-inspections-from-applicant?])
        use-qualifications?                   (<sub [::ias/use-qualifications?])
        checklists-enabled?                   (<sub [::gas/has-feature? :group.feature/inspection-checklists])
        group-feature-toggle                  (fn [feature-name & [feature-flag]]
                                                [feature-toggle/toggle-group-feature (util/mmap ctx feature-name tr feature-flag)])
        comply-by-date-notifications-enabled? (<sub [::ff/enabled? :feature-flag.id/comply-by-date-notifications])
        pre-issuance-inspections?             (<sub [::ff/enabled? :feature-flag.id/pre-issuance-inspections])
        multiple-bookings-authority-enabled?  (<sub [::gas/has-feature? :group.feature/multiple-inspection-bookings-authority])
        inspection-restrictions?              (<sub [::inspection-restrictions-available?])
        checklist-note-enabled?               (<sub [::checklist-subs/checklist-configuration-v2])
        group-comply-by-date-enabled?         (<sub [::gas/has-feature? :group.feature/comply-by-date])
        permit-types                          (<sub [::gas/group-permit-types])
        permit-types-without?                 #(empty? (set/intersection % permit-types))
        bookings-comply-by-date-enabled?      (<sub [::gas/has-feature? :group.feature/bookings-based-on-comply-by-date])]
    [acommon/section {}
     [inspection-phases/inspection-phase-selection ctx]
     (when (and can-hide-inspections? (not pre-issuance-inspections?))
       (group-feature-toggle :group.feature/applicant-visible-inspections))
     (group-feature-toggle :group.feature/inspection-time-tracking)
     (when (permit-types-without? ptc-model/permit-types-without-inspection-orders-available)
       (group-feature-toggle :group.feature/orders-for-inspections-enabled))
     (group-feature-toggle :group.feature/inspection-quick-pass)
     (group-feature-toggle :group.feature/hide-internal-notes)
     (group-feature-toggle :group.feature/inspection-priority :feature-flag.id/inspection-priority)
     [feature-toggle/toggle-group-feature (assoc ctx :feature-name :group.feature/inspection-checklists
                                                 :check-subfeature-issues? true
                                                 :tr tr)]
     (when checklists-enabled?
       [:<>
        [inspection-checklist-visibility ctx]
        (when checklist-note-enabled?
          (group-feature-toggle :group.feature/inspection-checklist-item-note))])
     (when (permit-types-without? ptc-model/permit-types-without-comply-by-date)
       [:<>
        (group-feature-toggle :group.feature/comply-by-date)
        (when (and group-comply-by-date-enabled? comply-by-date-notifications-enabled?)
          [comply-by-date-email-notification ctx])])
     (group-feature-toggle :group.feature/inspection-labels :feature-flag.id/inspection-labels)
     (when group-comply-by-date-enabled?
       (group-feature-toggle :group.feature/bookings-based-on-comply-by-date :feature-flag.id/deficiency-comply-by-bookings))
     (when bookings-comply-by-date-enabled?
       (group-feature-toggle :group.feature/select-deficiency-for-booking-by-default))
     (group-feature-toggle :group.feature/multiple-inspection-bookings-authority :feature-flag.id/multiple-inspection-bookings)
     (when (and multiple-bookings-authority-enabled?
                (permit-types-without? ptc-model/permit-types-without-multiple-inspection-booking-applicant))
       (group-feature-toggle :group.feature/multiple-inspection-bookings-applicant :feature-flag.id/multiple-inspection-bookings))
     (when inspection-restrictions?
       [:<>
        (group-feature-toggle :group.feature/inspection-restriction-role)
        (when use-qualifications?
          (group-feature-toggle :group.feature/inspection-restriction-qualification))])
     (group-feature-toggle :group.feature/recurring-inspections)
     (when-not (ff/enabled? :feature-flag.id/superdomain-working-day-calendar)
       [inspection-time-slots-editor/view ctx])
     [inspection-instructions-editor/view ctx]
     [:div
      [:h3.mb-2.pl-0 (tr [:phrase/list])]
      [phrases/default-category-selection (assoc ctx :label-loc :phrase/default-category)]]
     [inspection-freeze-time-editor/view tr]
     [numbering-configuration tr ctx group-feature-toggle]]))


(defn visit-section [tr id title content]
  [section/section
   id
   (util/mmap {:initially-collapsed? true}
              tr)
   (tr [title])
   content])

(defn visits-configuration [{:keys [tr] :as ctx}]
  [:div
   [visit-section
    tr
    "visit-types-section"
    :inspection/visit-types
    [visit-type/view ctx]]
   [visit-section
    tr
    "visit-results-section"
    :inspection/visit-results
    [visit-result/view ctx]]
   [visit-section
    tr
    "visit-reasons-section"
    :inspection/reasons
    [visit-result-detail/view ctx]]])

(defn view-tabbed [match]
  (let [tr                  (<sub [::gas/group-tr])
        ctx                 {:tr                 tr
                             :authority-group-id (<sub [::gas/group-id])
                             :phrase-context     :group/phrase-category-inspections}
        selected-tab        (or (<sub [::selected-tab]) :inspection/settings)
        checklists?         (<sub [::gas/has-feature? :group.feature/inspection-checklists])
        labels?             (<sub [::gas/has-feature? :group.feature/inspection-labels])
        visits-conf?        (<sub [::ias/visit-configuration-enabled?])
        use-qualifications? (<sub [::ias/use-qualifications?])
        quick-pick-list?    (<sub [::ff/enabled? :feature-flag.id/deficiency-quick-pick-list])
        tab-ids             (remove nil?
                                    [:inspection/settings
                                     :inspection.template/templates
                                     (when (or checklists?
                                               ;; Group features are not always available at the first render,
                                               ;; namely after logging in after expired session. This prevents
                                               ;; the problem of selecting non-existent tab.
                                               (= selected-tab :inspection/inspection-checklists))
                                       :inspection/inspection-checklists)
                                     (when (or labels?
                                               (= selected-tab :inspection/label-configuration))
                                       :inspection/label-configuration)
                                     (if visits-conf?
                                       :inspection/visits-configuration
                                       :inspection/visit-results)
                                     (when use-qualifications?
                                       :inspection/qualifications)
                                     :inspection.areas/title
                                     (when quick-pick-list?
                                       :inspection/quick-pick-list)])
        header              [ui-tabs/tabs
                             {:selected-id selected-tab
                              :aria-label  (tr [:group-admin/inspections])
                              :on-change   #(>evt [::select-tab %])
                              :tabs        (for [tab-id tab-ids]
                                             {:id    tab-id
                                              :label (tr [tab-id])})}]]
    [acommon/admin-page-v2 {:title          (tr [:group-admin/inspections])
                            :match          match
                            :header-content header}
     [(if (= selected-tab :inspection/inspection-checklists)
        acommon/section-title-wrapper-with-language-selector
        acommon/section-title-wrapper)
      {:title (tr [selected-tab])}
      (case selected-tab
        :inspection/settings [inspection-settings ctx]
        :inspection.template/templates [template-editor/view ctx]
        :inspection/inspection-checklists (if (<sub [::checklist-subs/checklist-configuration-v2])
                                            [inspection-checklists-v2/view ctx]
                                            [inspection-checklists/view ctx])
        :inspection/label-configuration [inspection-labels/view ctx]
        :inspection/visit-results [visit-result-detail/view ctx]
        :inspection/visits-configuration [visits-configuration ctx]
        :inspection/qualifications [qualification/view ctx]
        :inspection.areas/title [working-areas/view ctx]
        :inspection/quick-pick-list [quick-pick-deficiencies/view ctx]
        nil)]]))

(defn view [match]
  [view-tabbed match])

;;; Route

(def route
  {:name        :view/group.admin.inspections
   :view        view
   :public?     false
   :parameters  {:path {:group-id s/Int}}
   :own-layout? true
   :controllers [{:parameters {:path [:group-id]}
                  :start      (fn [params]
                                (>evt [::init (-> params :path :group-id)]))}]})
